// Protocolo do Hábito — escolher o livro. O livro é do leitor.
//
// A curadoria do app saiu. Ninguém começa um hábito com o título que um
// aplicativo recomendou: começa com o que já está na estante, na cabeceira, ou
// com aquele que comprou e nunca abriu.
//
// O que o protocolo pede continua valendo — só deixou de ser imposição e virou
// conselho na hora certa: o primeiro livro precisa TERMINAR, não impressionar.
// A taxa de conclusão é função do livro (83% num thriller comercial contra 28%
// numa não-ficção "importante"), e essa é a informação útil para quem escolhe.
//
// Três caminhos, do menor atrito para o maior. O manual nunca desaparece:
// automação que é a única porta deixa preso quem tem um livro sem capa legível.

import React, { useRef, useState } from 'react';
import { BookCover } from '../common';
import { Icon } from '../icons';
import { AuthSession, BookV2 } from '../../types';
import { habitoApi, OpcaoLivro } from './api';

const serif = 'Instrument Serif, Georgia, serif';

const eyebrow: React.CSSProperties = {
  fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase',
  color: 'var(--ink-3)', fontWeight: 600,
};

function hueDe(t: string) { return 18 + (t.length * 13) % 72; }

const CardOpcao: React.FC<{ o: OpcaoLivro; sel?: boolean; onClick: () => void }> = ({ o, sel, onClick }) => (
  <button onClick={onClick} style={{
    display: 'flex', gap: 13, width: '100%', textAlign: 'left', padding: 13,
    borderRadius: 18, marginBottom: 9, cursor: 'pointer', alignItems: 'center',
    background: sel ? 'var(--ember-soft)' : 'var(--paper-2)',
    border: `1px solid ${sel ? 'var(--ember)' : 'var(--line)'}`,
  }}>
    <BookCover book={{ title: o.titulo, coverUrl: o.capaUrl, coverHue: hueDe(o.titulo), coverStyle: 'paper' } as any} width={46} height={69} />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontFamily: serif, fontSize: 18, lineHeight: 1.15, color: 'var(--ink)' }}>{o.titulo}</div>
      <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2 }}>
        {o.autor || 'autor não identificado'}{o.paginas ? ` · ${o.paginas} páginas` : ''}
      </div>
    </div>
    {sel && <Icon name="check" size={17} color="var(--ember)" />}
  </button>
);

export const EscolherLivro: React.FC<{
  session: AuthSession | null;
  meusLivros?: BookV2[];
  paraFila?: boolean;
  onPronto: (livroId: string) => void;
  onCancelar?: () => void;
}> = ({ session, meusLivros = [], paraFila, onPronto, onCancelar }) => {
  const [aba, setAba] = useState<'foto' | 'busca' | 'manual' | 'estante'>('foto');
  const [lendo, setLendo] = useState(false);
  const [opcoes, setOpcoes] = useState<OpcaoLivro[]>([]);
  const [escolhida, setEscolhida] = useState<OpcaoLivro | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [busca, setBusca] = useState('');
  const [manual, setManual] = useState({ titulo: '', autor: '', paginas: '' });
  const arquivoRef = useRef<HTMLInputElement | null>(null);

  async function aoFotografar(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    setLendo(true); setAviso(null); setOpcoes([]); setEscolhida(null);
    try {
      const r = await habitoApi.lerCapa(session, f);
      if (!r.ok || !r.opcoes.length) {
        setAviso('Não consegui ler essa capa. Tente com mais luz, ou use a busca por nome.');
        setAba('busca');
      } else {
        setOpcoes(r.opcoes);
        setEscolhida(r.opcoes[0]);
        if (r.lido && r.lido.confianca === 'baixa') {
          setAviso('A capa estava difícil de ler — confira o título antes de continuar.');
        }
      }
    } catch {
      setAviso('Não consegui ler essa foto. Use a busca por nome ou cadastre à mão.');
      setAba('busca');
    } finally {
      setLendo(false);
      if (arquivoRef.current) arquivoRef.current.value = '';
    }
  }

  async function buscar() {
    if (busca.trim().length < 2) return;
    setLendo(true); setOpcoes([]); setEscolhida(null); setAviso(null);
    try {
      const r = await habitoApi.buscarLivros(session, busca.trim());
      // A linha honesta para quem esperava um PDF (CLAREZA §3).
      if (!r.length) { setAviso('Não achei esse título. Você pode cadastrar à mão. O Forjamente não tem livros pra baixar. Se ainda não tem este, dá pra pegar numa biblioteca pública ou comprar onde preferir — e voltar aqui pra registrar.'); setAba('manual'); }
      setOpcoes(r);
    } catch { setAviso('A busca falhou. Cadastre à mão que funciona igual.'); }
    finally { setLendo(false); }
  }

  async function confirmar(o: OpcaoLivro) {
    setSalvando(true); setAviso(null);
    try {
      const r = await habitoApi.criarLivro(session, { ...o, paraFila });
      onPronto(r.livroId);
    } catch (e: any) {
      setAviso(e?.message || 'Não consegui salvar esse livro.');
    } finally { setSalvando(false); }
  }

  const abas = [
    { id: 'foto', r: 'Foto da capa', i: 'camera' },
    { id: 'busca', r: 'Buscar', i: 'search' },
    { id: 'manual', r: 'À mão', i: 'pen' },
    ...(meusLivros.length ? [{ id: 'estante', r: 'Minha estante', i: 'library' }] : []),
  ] as const;

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {abas.map((t) => (
          <button key={t.id} onClick={() => { setAba(t.id as any); setAviso(null); }} style={{
            flex: '1 1 auto', padding: '9px 12px', borderRadius: 999, fontSize: 12.5, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            border: `1px solid ${aba === t.id ? 'var(--ember)' : 'var(--line)'}`,
            background: aba === t.id ? 'var(--ember-soft)' : 'transparent',
            color: aba === t.id ? 'var(--ember)' : 'var(--ink-3)',
          }}>
            <Icon name={t.i} size={13} color={aba === t.id ? 'var(--ember)' : 'var(--ink-4)'} />
            {t.r}
          </button>
        ))}
      </div>

      {/* ── Foto ─────────────────────────────────────────────────────── */}
      {aba === 'foto' && (
        <div>
          <input
            ref={arquivoRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={aoFotografar}
            style={{ display: 'none' }}
          />
          <button
            onClick={() => arquivoRef.current && arquivoRef.current.click()}
            disabled={lendo}
            style={{
              width: '100%', padding: '30px 20px', borderRadius: 20, cursor: 'pointer',
              border: '1px dashed var(--line-strong)', background: 'var(--paper-2)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
            }}
          >
            <div style={{
              width: 54, height: 54, borderRadius: '50%', background: 'var(--ember-soft)',
              display: 'grid', placeItems: 'center',
              animation: lendo ? 'habLendoCapa 1.6s ease-in-out infinite' : 'none',
            }}>
              <Icon name="camera" size={24} color="var(--ember)" />
            </div>
            <div style={{ fontFamily: serif, fontSize: 19, color: 'var(--ink)' }}>
              {lendo ? 'Lendo a capa…' : 'Fotografe a capa'}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-3)', textAlign: 'center', lineHeight: 1.45, maxWidth: 260 }}>
              {lendo
                ? 'Isso leva alguns segundos.'
                : 'Pegue o livro que você já tem. Eu leio o título e o autor e busco o resto.'}
            </div>
          </button>
          <style>{`@keyframes habLendoCapa { 0%,100% { transform: scale(1); opacity:.7 } 50% { transform: scale(1.08); opacity:1 } }`}</style>
        </div>
      )}

      {/* ── Busca ────────────────────────────────────────────────────── */}
      {aba === 'busca' && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') buscar(); }}
            placeholder="Título do livro"
            style={{
              flex: 1, padding: '13px 15px', borderRadius: 14, fontSize: 15,
              background: 'var(--paper-2)', border: '1px solid var(--line)', color: 'var(--ink)',
            }}
          />
          <button onClick={buscar} disabled={lendo} style={{
            padding: '13px 18px', borderRadius: 14, border: 'none',
            background: 'var(--ember)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}>{lendo ? '…' : 'Buscar'}</button>
        </div>
      )}

      {/* ── Manual ───────────────────────────────────────────────────── */}
      {aba === 'manual' && (
        <div style={{ marginBottom: 14 }}>
          {([['titulo', 'Título', 'O nome do livro'], ['autor', 'Autor', 'Quem escreveu'], ['paginas', 'Páginas', 'Quantas páginas tem']] as const).map(([k, r, ph]) => (
            <div key={k} style={{ marginBottom: 11 }}>
              <div className="mono" style={{ ...eyebrow, marginBottom: 6 }}>{r}</div>
              <input
                data-campo={k}
                value={(manual as any)[k]}
                inputMode={k === 'paginas' ? 'numeric' : 'text'}
                onChange={(e) => setManual((m) => ({ ...m, [k]: e.target.value }))}
                placeholder={ph}
                style={{
                  width: '100%', padding: '13px 15px', borderRadius: 14, fontSize: 15,
                  background: 'var(--paper-2)', border: '1px solid var(--line)', color: 'var(--ink)',
                }}
              />
            </div>
          ))}
          <button
            onClick={() => confirmar({
              titulo: manual.titulo.trim(),
              autor: manual.autor.trim(),
              paginas: parseInt(manual.paginas, 10) || null,
              capaUrl: null,
            })}
            disabled={salvando || manual.titulo.trim().length < 2}
            style={{
              width: '100%', padding: '15px 20px', borderRadius: 999, border: 'none', marginTop: 4,
              background: manual.titulo.trim().length >= 2 ? 'var(--ember)' : 'var(--paper-3)',
              color: manual.titulo.trim().length >= 2 ? '#fff' : 'var(--ink-4)',
              fontSize: 15.5, fontWeight: 600, cursor: 'pointer',
            }}
          >{salvando ? 'um instante…' : 'É esse'}</button>
          <div style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 8, lineHeight: 1.45 }}>
            Não sabe quantas páginas? Deixe em branco — dá para acertar depois, e isso
            não muda nada no programa.
          </div>
        </div>
      )}

      {/* ── Estante ──────────────────────────────────────────────────── */}
      {aba === 'estante' && (
        <div>
          {meusLivros.slice(0, 20).map((b) => (
            <button key={b.id} disabled={salvando} onClick={async () => {
              setSalvando(true);
              try {
                if (paraFila) await habitoApi.addFila(session, b.id);
                else await habitoApi.escolherLivro(session, { bookId: b.id });
                onPronto(b.id);
              } catch (e: any) { setAviso(e?.message || 'Não consegui salvar.'); }
              finally { setSalvando(false); }
            }} style={{
              display: 'flex', gap: 12, width: '100%', textAlign: 'left', padding: 11,
              borderRadius: 16, marginBottom: 8, cursor: 'pointer', alignItems: 'center',
              background: 'var(--paper-2)', border: '1px solid var(--line)',
            }}>
              <BookCover book={b as any} width={40} height={60} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14.5, color: 'var(--ink)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.title}</div>
                <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{b.author}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {aviso && (
        <div style={{
          fontSize: 13, lineHeight: 1.5, color: 'var(--ink-2)', marginBottom: 12,
          padding: '11px 13px', borderRadius: 13, background: 'var(--paper-2)', border: '1px solid var(--line)',
        }}>{aviso}</div>
      )}

      {/* Resultados de foto/busca */}
      {opcoes.length > 0 && (
        <div style={{ marginTop: 4 }}>
          {/* A PERGUNTA EM TAMANHO DE TÍTULO (02/10/2026). Em letra miúda, por
              cima de uma lista de capas, a tela parecia o resultado de uma loja
              — e quem esperava um catálogo via a confirmação do que esperava. */}
          <div data-clareza="qual-e-o-seu" style={{ fontFamily: serif, fontSize: 21, lineHeight: 1.2, color: 'var(--ink)', marginBottom: 6 }}>
            {opcoes.length === 1 ? 'É este o seu?' : 'Qual deles é o seu?'}
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink-3)', marginBottom: 12 }}>
            Escolha o que mais parece com a capa que você tem em mãos — não precisa ser edição idêntica.
          </div>
          {opcoes.map((o, i) => (
            <CardOpcao key={i} o={o} sel={escolhida === o} onClick={() => setEscolhida(o)} />
          ))}
          <button
            onClick={() => escolhida && confirmar(escolhida)}
            disabled={!escolhida || salvando}
            style={{
              width: '100%', padding: '15px 20px', borderRadius: 999, border: 'none', marginTop: 6,
              background: escolhida ? 'var(--ember)' : 'var(--paper-3)',
              color: escolhida ? '#fff' : 'var(--ink-4)',
              fontSize: 15.5, fontWeight: 600, cursor: 'pointer',
            }}
          >{salvando ? 'um instante…' : 'É esse'}</button>
          <button onClick={() => { setAba('manual'); setOpcoes([]); }} style={{
            width: '100%', background: 'none', border: 'none', color: 'var(--ink-3)',
            fontSize: 13, padding: '11px 0 0', cursor: 'pointer',
          }}>Nenhum é o meu — cadastrar à mão</button>
        </div>
      )}

      {onCancelar && (
        <button onClick={onCancelar} style={{
          width: '100%', background: 'none', border: 'none', color: 'var(--ink-3)',
          fontSize: 13.5, padding: '14px 0 0', cursor: 'pointer',
        }}>Cancelar</button>
      )}
    </div>
  );
};

export default EscolherLivro;
